/**
 * Wspólne części formatek dokumentów Symfonii (sprzedaż, magazyn, zamówienie, kasa).
 *
 * Wszystkie cztery podglądy mają ten sam układ: nagłówek z numerem i wydrukiem, dwie kolumny
 * pól, zakładki, pasek sum. Różnią się polami i zestawem zakładek — dlatego dzielimy KLOCKI,
 * a nie cały ekran: wspólny szkielet z parametrami na każde pole byłby trudniejszy do czytania
 * niż cztery jawne formatki złożone z tych samych elementów.
 *
 * WSZYSTKO JEST TYLKO DO ODCZYTU i nie jest to ograniczenie do zdjęcia: dokumentu wystawionego
 * w Symfonii nie edytuje się przez WebAPI, a źródłem prawdy jest Symfonia.
 */

import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { HmDocumentAddress } from '@saas/shared';
import { isAxiosError } from 'axios';
import { Download, FileWarning, Printer } from 'lucide-react';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { useTabsOptional } from '@/contexts/TabContext';
import { api } from '@/lib/api';

/** Pole formatki — etykieta + wartość w wyłączonej kontrolce (układ jak w Handlu). */
export function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="grid grid-cols-[110px_1fr] items-center gap-2">
      <span className="text-sm text-text-secondary">{label}</span>
      <div className="rounded-md border border-border-primary bg-bg-tertiary px-2.5 py-1.5 text-sm text-text-primary">
        {value && String(value).trim() !== '' ? value : '—'}
      </div>
    </div>
  );
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
      {children}
    </section>
  );
}

export function day(value: string | null): string {
  return value ? value.slice(0, 10) : '—';
}

export function addressLines(a: HmDocumentAddress | null): string[] {
  if (!a) return [];
  const street = [a.street, a.houseNo, a.apartmentNo ? `/${a.apartmentNo}` : null]
    .filter(Boolean)
    .join(' ');
  const city = [a.postCode, a.city].filter(Boolean).join(' ');
  return [a.name, a.taxId ? `NIP ${a.taxId}` : null, street, city].filter(
    (x): x is string => !!x && x.trim() !== '',
  );
}

/**
 * Pobranie wydruku. PDF idzie przez naszego klienta HTTP (z nagłówkiem autoryzacji) do postaci
 * binarnej, a potem pokazujemy go z adresu lokalnego. Zwykły `<iframe src="/api/...">` NIE
 * zadziała — przeglądarka wyśle wtedy żądanie bez tokenu i dostanie odmowę.
 */
export function usePdfPreview(path: string) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  /** `extraQuery` niesie ustawienia wydruku (szablon, nagłówek, stopka) wybrane w oknie. */
  async function show(extraQuery = '') {
    const target = extraQuery ? `${path}&${extraQuery}` : path;
    setLoading(true);
    try {
      const res = await api.get<Blob>(target, { responseType: 'blob' });
      setUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(res.data);
      });
    } finally {
      setLoading(false);
    }
  }

  /** Zamknięcie zwalnia adres lokalny — inaczej plik wisi w pamięci karty do jej zamknięcia. */
  function close() {
    setUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }

  return { url, loading, show, close };
}

/**
 * Podmienia etykietę karty na numer dokumentu, gdy ten się wczyta.
 *
 * Etykietę początkową („Dokument") nadaje słownik kart ze ścieżki — numer znamy dopiero po
 * odpowiedzi Symfonii. Trafiamy w kartę PO ścieżce, nie w aktywną: panele są keep-alive, więc
 * w chwili wczytania aktywna może być już inna karta. Poza powłoką kart (testy) to nic nie robi.
 */
export function useDocumentTabLabel(basePath: string, documentNumber: string | undefined) {
  const tabCtx = useTabsOptional();
  useEffect(() => {
    if (!tabCtx || !documentNumber) return;
    const tab = tabCtx.tabs.find((t) => t.basePath === basePath);
    if (tab && tab.label !== documentNumber) tabCtx.updateTabLabel(tab.id, documentNumber);
  }, [tabCtx, basePath, documentNumber]);
}

/** Nagłówek formatki: numer, podpis „tylko do odczytu" i wydruk (gdy trasa PDF istnieje). */
export function DetailHeader({
  documentNumber,
  onPrint,
  printLoading,
}: {
  documentNumber: string;
  onPrint?: () => void;
  printLoading?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold text-text-primary">{documentNumber}</h1>
        <p className="text-sm text-text-secondary">{t('tenant.handelMagazyn.detail.readOnly')}</p>
      </div>
      {onPrint && (
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Printer className="h-4 w-4" />}
          loading={printLoading}
          onClick={onPrint}
        >
          {t('tenant.handelMagazyn.detail.pdf')}
        </Button>
      )}
    </div>
  );
}

/**
 * Ekran błędu formatki. `DOCUMENT_NOT_FOUND` ma osobną treść, bo najczęstsza jego przyczyna nie
 * jest awarią: Symfonia trzyma część dokumentów (np. korekty sprzedaży) pod innym zasobem.
 */
export function DetailError({ error }: { error: unknown }) {
  const { t } = useTranslation();
  const code = isAxiosError(error)
    ? ((error.response?.data as { error?: string } | undefined)?.error ?? null)
    : null;
  return (
    <div className="p-4">
      <div className="rounded-md border border-border-primary bg-bg-secondary p-6 text-center">
        <FileWarning className="mx-auto mb-2 h-6 w-6 text-text-tertiary" />
        <p className="text-sm font-medium text-text-primary">
          {code === 'DOCUMENT_NOT_FOUND'
            ? t('tenant.handelMagazyn.detail.notFound')
            : t('tenant.handelMagazyn.problem.UNKNOWN')}
        </p>
        <p className="mt-1 text-xs text-text-secondary">
          {t('tenant.handelMagazyn.detail.notFoundHint')}
        </p>
      </div>
    </div>
  );
}

/** Zakładki formatki — pozycje zwykle jako pierwsze, bo po nie najczęściej się wchodzi. */
export function DetailTabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: readonly T[];
  active: T;
  onChange: (tab: T) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-4 border-b border-border-primary">
      <nav className="flex gap-1">
        {tabs.map((key) => (
          <Button
            key={key}
            variant="ghost"
            size="sm"
            onClick={() => onChange(key)}
            className={`rounded-none border-b-2 ${
              active === key
                ? 'border-accent-600 font-medium text-text-primary'
                : 'border-transparent'
            }`}
          >
            {t(`tenant.handelMagazyn.detail.tabs.${key}`)}
          </Button>
        ))}
      </nav>
    </div>
  );
}

export function DetailTabBody({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-0 flex-1 overflow-auto rounded-b-md border border-t-0 border-border-primary bg-bg-secondary p-3">
      {children}
    </div>
  );
}

/** Blok adresowy w zakładce „Dane adresowe". */
export function AddressBlock({
  title,
  address,
}: {
  title: string;
  address: HmDocumentAddress | null;
}) {
  const lines = addressLines(address);
  return (
    <div>
      <h3 className="mb-1 text-sm font-semibold text-text-primary">{title}</h3>
      {lines.length ? (
        lines.map((line) => (
          <p key={line} className="text-sm text-text-secondary">
            {line}
          </p>
        ))
      ) : (
        <p className="text-sm text-text-tertiary">—</p>
      )}
    </div>
  );
}

/** Pasek sum pod zakładkami — pary etykieta/wartość, ostatnia wyróżniona. */
export function TotalsBar({
  items,
}: {
  items: { label: string; value: string; strong?: boolean }[];
}) {
  return (
    <div className="mt-2 flex items-center justify-end gap-6 rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-sm">
      {items.map((it) => (
        <span key={it.label} className="text-text-secondary">
          {it.label}:{' '}
          <span
            className={
              it.strong ? 'font-semibold text-text-primary' : 'font-medium text-text-primary'
            }
          >
            {it.value}
          </span>
        </span>
      ))}
    </div>
  );
}

/**
 * Podgląd wydruku w MODALU, z wydrukiem i zapisem do pliku.
 *
 * Wcześniej wydruk dokładał się POD formatką i trzeba go było doscrollować — przy dokumencie
 * z kilkoma zakładkami ginął poniżej ekranu. Modal stawia go na wierzchu, a zamknięcie wraca
 * do dokumentu.
 *
 * DRUKOWANIE: najpierw próbujemy wydrukować zawartość ramki. Przeglądarki renderują PDF własnym
 * czytnikiem i nie każdy wystawia `print()` — dlatego przy niepowodzeniu otwieramy plik w nowej
 * karcie, gdzie czytnik ma własny przycisk wydruku. Cicha porażka byłaby najgorsza z możliwych:
 * użytkownik klika „Drukuj" i nie dzieje się nic.
 */
export function PdfPreviewModal({
  url,
  title,
  onClose,
}: {
  url: string | null;
  title: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const frameRef = useRef<HTMLIFrameElement>(null);

  function print() {
    const frame = frameRef.current;
    try {
      if (frame?.contentWindow) {
        frame.contentWindow.focus();
        frame.contentWindow.print();
        return;
      }
    } catch {
      // czytnik PDF nie wystawia `print()` — spadamy do nowej karty
    }
    if (url) window.open(url, '_blank', 'noopener');
  }

  function download() {
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    // Numery dokumentów mają ukośniki („0315/26/FVS") — w nazwie pliku to katalogi, nie tekst.
    link.download = `${title.replace(/[\\/:*?"<>|]/g, '-')}.pdf`;
    link.click();
  }

  return (
    <Modal
      open={!!url}
      onClose={onClose}
      title={t('tenant.handelMagazyn.detail.pdf')}
      description={title}
      size="2xl"
      fixedHeight
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} data-testid="hm-pdf-close">
            {t('common.close')}
          </Button>
          <Button
            variant="secondary"
            leftIcon={<Download className="h-4 w-4" />}
            onClick={download}
            data-testid="hm-pdf-download"
          >
            {t('tenant.handelMagazyn.detail.savePdf')}
          </Button>
          <Button
            variant="primary"
            leftIcon={<Printer className="h-4 w-4" />}
            onClick={print}
            data-testid="hm-pdf-print"
          >
            {t('tenant.handelMagazyn.toolbar.print')}
          </Button>
        </div>
      }
    >
      {url && (
        <iframe
          ref={frameRef}
          src={url}
          title={title}
          className="h-full min-h-[60vh] w-full rounded-md border border-border-primary"
          data-testid="hm-pdf-frame"
        />
      )}
    </Modal>
  );
}
